import type React from "react";
import type { ConnectionRequestSummary } from "@shared/schemas/connection-request.schemas";
import {
  ConnectionRequestIdPublic,
  type EventParticipantIdPublic,
} from "@shared/schemas/branded-id.schemas";
import { i18n } from "@frontend/i18n";
import { BackButton } from "@planta-v2/pds-ui/back-button";
import { SentRequestCard } from "./SentRequestCard";
import { DirectoryListSkeleton } from "../state/DirectoryListSkeleton";
import {
  DirectoryEmptyState,
  SENT_REQUESTS_STATE,
} from "../state/DirectoryEmptyState";

interface SentRequestsViewProps {
  readonly requests: readonly ConnectionRequestSummary[] | undefined;
  readonly isLoading: boolean;
  readonly onBack: () => void;
  readonly onViewProfile: (
    participantIdPublic: EventParticipantIdPublic,
  ) => void;
}

export function SentRequestsView({
  requests,
  isLoading,
  onBack,
  onViewProfile,
}: SentRequestsViewProps): React.JSX.Element {
  return (
    <div
      data-testid="sent-requests-overlay"
      className="flex h-dvh flex-col bg-background"
    >
      {/* Header with back navigation */}
      <div className="flex-shrink-0 p-4">
        <BackButton
          data-testid="sent-requests-back-button"
          onClick={onBack}
          label={i18n.t("user.directory.requests.sent.back")}
        />
      </div>

      {/* Title */}
      <div className="px-4">
        <h2 data-testid="sent-requests-title" className="page-title mb-4">
          {i18n.t("user.directory.requests.sent.title")}
        </h2>
      </div>

      {/* Content: Loading / Empty / List */}
      <div className="flex-1 overflow-y-auto px-4 pb-[calc(1rem+env(safe-area-inset-bottom))]">
        {isLoading ? (
          <DirectoryListSkeleton variant="card" />
        ) : requests === undefined || requests.length === 0 ? (
          <DirectoryEmptyState tab={SENT_REQUESTS_STATE} />
        ) : (
          <div data-testid="sent-requests-list">
            {requests.map((request) => (
              <SentRequestCard
                key={ConnectionRequestIdPublic.unwrap(request.idPublic)}
                request={request}
                onViewProfile={onViewProfile}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
